import { Link } from "react-router-dom";
import { BadgeCheck, Users, UtensilsCrossed, ArrowRight } from "lucide-react";
import { SectionHead, Seal, VertKanji } from "../components/Chrome";

export default function TentangPage() {
  return (
    <>
      <section className="border-b-2 border-ink bg-paper" data-testid="tentang-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 py-16 sm:px-6 lg:grid-cols-12 lg:px-12 lg:py-24">
          <div className="lg:col-span-7">
            <nav aria-label="Breadcrumb" className="text-xs font-semibold uppercase tracking-[0.25em] text-sumi">
              <Link to="/" data-testid="breadcrumb-home" className="transition-colors hover:text-hanko">
                Beranda
              </Link>
              <span aria-hidden="true"> / </span>
              <span className="text-hanko">Tentang</span>
            </nav>
            <h1 className="mt-6 font-display text-4xl font-bold tracking-tight text-ink sm:text-5xl">
              Tentang Umaku Sushi
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-sumi">
              Nama “Umaku” berasal dari kata Jepang <span className="font-display font-bold text-ink">旨く</span> —
              lezat, dibuat dengan terampil. Itulah janji sederhana yang kami jaga di setiap piring: hidangan
              Jepang autentik, bahan segar, dan potongan yang tidak pelit.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-sumi">
              Sebagai kedai sushi bersertifikat halal MUI, Umaku Sushi menjadi pilihan keluarga dan pencinta sushi
              di Jabodetabek yang ingin menikmati hidangan Jepang tanpa ragu.
            </p>
          </div>
          <div className="relative lg:col-span-5">
            <img
              src="/images/hero-salmon-roll.jpg"
              alt="Salmon roll dengan isian melimpah khas Umaku Sushi"
              width="1600"
              height="1067"
              loading="lazy"
              data-testid="tentang-image-roll"
              className="aspect-[4/3] w-full border-2 border-ink object-cover"
            />
            <Seal className="absolute -bottom-5 -left-4 h-16 w-16 rotate-6 text-2xl" />
          </div>
        </div>
      </section>

      <section className="bg-paper py-16 lg:py-24" data-testid="tentang-philosophy">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <SectionHead overline="Filosofi" title="Umami di Atas Segalanya" jp="旨味" />
          <div className="mt-10 grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="text-base leading-relaxed text-sumi">
                Alih-alih menutupi rasa dengan bumbu berlebih, Umaku mengandalkan umami — rasa gurih dan manis
                alami yang keluar dari kesegaran bahan itu sendiri. Salmon dipilih dari kualitas premium, lalu
                diiris tebal agar tekstur dan rasanya benar-benar terasa.
              </p>
              <p className="mt-4 text-base leading-relaxed text-sumi">
                Pendekatan ini terasa di seluruh menu: dari Sake Salmon Sashimi yang menjadi favorit, roll fusion
                seperti Salmon Chizu Roll, sampai Gekikara Hotto Ramen bagi pencinta pedas di cabang Bintaro.
              </p>
              <ul className="mt-8 space-y-4">
                <li className="flex items-start gap-3 border-l-2 border-hanko pl-4">
                  <BadgeCheck className="mt-0.5 h-5 w-5 shrink-0 text-hanko" aria-hidden="true" />
                  <p className="text-sm leading-relaxed text-sumi">
                    <span className="font-bold text-ink">Bersertifikat halal MUI.</span> Seluruh cabang menjalankan
                    standar halal yang sama.
                  </p>
                </li>
                <li className="flex items-start gap-3 border-l-2 border-hanko pl-4">
                  <UtensilsCrossed className="mt-0.5 h-5 w-5 shrink-0 text-hanko" aria-hidden="true" />
                  <p className="text-sm leading-relaxed text-sumi">
                    <span className="font-bold text-ink">Suasana kedai Jepang.</span> Interior simpel dan minimalis
                    di tiap cabang, agar fokus tetap pada hidangan.
                  </p>
                </li>
                <li className="flex items-start gap-3 border-l-2 border-hanko pl-4">
                  <Users className="mt-0.5 h-5 w-5 shrink-0 text-hanko" aria-hidden="true" />
                  <p className="text-sm leading-relaxed text-sumi">
                    <span className="font-bold text-ink">Ramai di jam makan siang.</span> Datang lebih awal atau
                    manfaatkan waiting list WhatsApp di cabang Bintaro.
                  </p>
                </li>
              </ul>
            </div>
            <div className="relative lg:col-span-5">
              <img
                src="/images/sushi-flatlay.jpg"
                alt="Aneka sushi dan sashimi tersaji di atas papan batu"
                width="1600"
                height="900"
                loading="lazy"
                data-testid="tentang-image-flatlay"
                className="aspect-[4/3] w-full border-2 border-ink object-cover lg:aspect-[3/4]"
              />
              <VertKanji
                text="新鮮"
                className="absolute -right-3 top-6 border-2 border-ink bg-paper px-2 py-3 text-lg font-bold text-ink shadow-[4px_4px_0_rgba(24,24,27,1)]"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="border-t-2 border-ink bg-parchment py-14" data-testid="tentang-cta">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-4 sm:px-6 lg:px-12">
          <h2 className="font-display text-2xl font-bold text-ink">Penasaran dengan rasanya?</h2>
          <Link
            to="/menu/"
            data-testid="tentang-menu-cta"
            className="inline-flex items-center gap-2 bg-ink px-6 py-3.5 text-sm font-bold text-paper transition-colors hover:bg-hanko"
          >
            Jelajahi Menu
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>
    </>
  );
}
